import { css } from "@emotion/css";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";

import { useColors } from "../../hooks/useColors";
import { Dialog } from "../modal/Dialog";
import { DialogContent } from "../modal/DialogContent";
import { DialogHeader } from "../modal/DialogHeader";
import { LinkButton } from "../link/LinkButton";
import { Modal } from "../modal/Modal";
import { ModalTrigger } from "../modal/ModalTrigger";
import { Icon } from "../../foundations/icons/Icon/Icon";

interface Props {
  linkText?: string;
  modalHeading?: string;
  modalBody?: string;
  modalBodyAccepted?: string[];
  modalBodyNotAccepted?: string[];
}

export function InputModal(props: Props) {
  const {
    linkText,
    modalHeading,
    modalBody,
    modalBodyAccepted,
    modalBodyNotAccepted,
  } = props;
  const { t } = useTranslation();
  const colors = useColors();
  const textBase = colors["text-base"];
  const borderBase = colors["border-base"];
  const markdownClassName = useMemo(
    () =>
      css({
        "& a": {
          color: textBase,
        },
        "& p:first-child": {
          marginTop: 0,
        },
        "& hr": {
          borderColor: borderBase,
        },
      }),
    [textBase, borderBase]
  );
  if (
    linkText === undefined ||
    modalHeading === undefined ||
    modalBody === undefined
  ) {
    return null;
  }

  function renderList(
    title: string,
    iconName: "check_circle" | "cancel",
    iconColor: "icon-success" | "icon-danger",
    items?: string[]
  ) {
    if (!items?.length) return null;
    return (
      <div className={styles.listBlock}>
        <div className={styles.heading}>
          <span className={styles.iconWrapper}>
            <Icon
              name={iconName}
              color={iconColor}
              className={styles.inlineIcon}
            />
          </span>
          {title}
        </div>
        <ul className={styles.list}>
          {items.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <ModalTrigger
      trigger={<LinkButton testID="open-modal-link">{linkText}</LinkButton>}
    >
      <Modal>
        <Dialog width={560}>
          {() => (
            <>
              <DialogHeader>{modalHeading}</DialogHeader>
              <DialogContent>
                <ReactMarkdown className={markdownClassName}>
                  {modalBody}
                </ReactMarkdown>
                {(modalBodyAccepted?.length ||
                  modalBodyNotAccepted?.length) && (
                  <div className={styles.listContainer}>
                    {renderList(
                      t("Acceptable:", "Acceptable:"),
                      "check_circle",
                      "icon-success",
                      modalBodyAccepted
                    )}
                    {renderList(
                      t("Unacceptable:", "Unacceptable:"),
                      "cancel",
                      "icon-danger",
                      modalBodyNotAccepted
                    )}
                  </div>
                )}
              </DialogContent>
            </>
          )}
        </Dialog>
      </Modal>
    </ModalTrigger>
  );
}

const styles = {
  listContainer: css({
    display: "flex",
    gap: "2rem",
    marginTop: "1.5rem",
  }),
  listBlock: css({
    minWidth: 220,
  }),
  heading: css({
    display: "flex",
    alignItems: "center",
    fontWeight: 600,
    marginBottom: 4,
  }),
  iconWrapper: css({
    marginRight: 6,
  }),
  list: css({
    margin: 0,
    paddingLeft: 24,
  }),
  inlineIcon: css({
    verticalAlign: "middle",
  }),
};
